// Google Ads base tag + deployment conversion for the Metrly console.
//
// The shared Google tag is loaded once from src/main.tsx (loadGoogleTag).
//
// Deployment conversions use a pending-deployment state machine so the
// conversion still fires even if the 'install completed' WebSocket event is
// missed (fired before ServerPage subscribes, after navigation, or on an
// event path this component does not observe):
//   1. recordPendingDeployment(serverUuid) is called right after either the
//      marketplace or registration API returns a new server, persisting a
//      short-lived marker in localStorage.
//   2. reportGoogleAdsDeploymentConversion(serverUuid) only queues a
//      conversion when an unexpired pending marker exists. It is called both
//      from the 'install completed' WebSocket handler and from a status
//      fallback that runs when the server is already confirmed installed.
//   3. transaction_id (server-deployment-<uuid>) plus a 72h localStorage
//      dedup key guarantee the conversion is queued at most once per server,
//      surviving remounts and browser restarts. Google's transaction_id is
//      the final server-side duplicate protection.
// See docs/GOOGLE_ADS.md for details.

import { getGoogleAnalyticsId, isGoogleAnalyticsEnabled } from './googleAnalytics'
import { hasMeasurementConsent } from './measurementConsent'

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
    __RUNTIME_CONFIG__?: { STRIPE_PUBLISHABLE_KEY?: string }
  }
}

const PRODUCTION_HOSTNAMES = new Set<string>(['console.metrly.com'])

const LINKER_DOMAINS = ['metrly.com', 'www.metrly.com', 'console.metrly.com']

const PENDING_PREFIX = 'google_ads_pending_deployment:'
const DEDUP_PREFIX = 'google_ads_deployment_conversion:'
// The registration verification window is 48 hours. Keep the marker for 72
// hours so a customer who verifies near the deadline can still report the
// already-installed server when they reach its page.
const CONVERSION_TTL_MS = 72 * 60 * 60 * 1000

type GoogleAdsSkipReason =
  | 'disabled'
  | 'wrong_hostname'
  | 'missing_ads_id'
  | 'missing_conversion_label'
  | 'no_pending_deployment'
  | 'pending_deployment_expired'
  | 'already_reported'
  | 'missing_server_uuid'
  | 'measurement_denied'

function getRuntimeConfig(): Record<string, unknown> {
  if (typeof window === 'undefined') return {}
  return window.__RUNTIME_CONFIG__ || {}
}

function readConfigValue(key: string): string {
  const runtime = getRuntimeConfig()
  const runtimeVal = runtime[key]
  if (runtimeVal !== undefined && runtimeVal !== null && runtimeVal !== '') {
    return String(runtimeVal)
  }
  return ''
}

export function getGoogleAdsId(): string {
  return readConfigValue('GOOGLE_ADS_ID')
}

export function getGoogleAdsDeploymentLabel(): string {
  return readConfigValue('GOOGLE_ADS_DEPLOYMENT_CONVERSION_LABEL')
}

export function isProductionHostname(): boolean {
  if (typeof window === 'undefined') return false
  return PRODUCTION_HOSTNAMES.has(window.location.hostname)
}

export function isGoogleAdsEnabled(): boolean {
  return isGoogleAdsTagEnabled() && Boolean(getGoogleAdsDeploymentLabel())
}

export function isGoogleAdsTagEnabled(): boolean {
  if (typeof window === 'undefined') return false
  if (!hasMeasurementConsent()) return false
  if (!isProductionHostname()) return false
  const enabled = readConfigValue('GOOGLE_ADS_ENABLED')
  if (enabled !== 'true') return false
  return Boolean(getGoogleAdsId())
}

// Production-safe debug mode, enabled only with ?google_ads_debug=1 on the URL.
export function isGoogleAdsDebug(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return new URLSearchParams(window.location.search).get('google_ads_debug') === '1'
  } catch {
    return false
  }
}

export function googleAdsDebug(message: string, data?: Record<string, unknown>): void {
  if (!isGoogleAdsDebug()) return
  console.info(`[Google Ads] ${message}`, data ?? {})
}

// Reset between tests. Exported for the test suite only.
export function __resetGoogleAdsForTests(): void {
  hasLoaded = false
}

let hasLoaded = false

export function loadGoogleTag(): void {
  if (typeof window === 'undefined') return
  if (!hasMeasurementConsent()) return
  if (hasLoaded) return
  const googleAdsTagEnabled = isGoogleAdsTagEnabled()
  const googleAnalyticsEnabled = isGoogleAnalyticsEnabled()
  if (!googleAdsTagEnabled && !googleAnalyticsEnabled) return

  // Use exactly one bootstrap/config destination. The connected Ads tag is
  // preferred in production; GA4 is the independent fallback if Ads is off.
  const tagId = googleAdsTagEnabled ? getGoogleAdsId() : getGoogleAnalyticsId()
  if (!tagId) return
  hasLoaded = true

  window.dataLayer = window.dataLayer || []
  const gtag: (...args: unknown[]) => void = function gtag(...args: unknown[]) {
    window.dataLayer!.push(args)
  }
  window.gtag = window.gtag || gtag

  const existing = document.querySelector(
    'script[src^="https://www.googletagmanager.com/gtag/js"]'
  )
  if (!existing) {
    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(tagId)}`
    document.head.appendChild(script)
  }

  // Configure the cross-domain linker BEFORE js/config so the sitewide tag
  // decorates outbound navigation with the _gl parameter when needed.
  // https://developers.google.com/tag-platform/devguides/cross-domain
  window.gtag('consent', 'default', {
    analytics_storage: 'granted', ad_storage: 'granted',
    ad_user_data: 'granted', ad_personalization: 'denied',
  })
  window.gtag('set', 'linker', { domains: LINKER_DOMAINS })
  window.gtag('js', new Date())
  // GA4 enhanced-measurement history events are disabled in the property.
  // Suppress the config command's initial page view as well: the console emits
  // only sanitized, allowlisted acquisition/auth page views explicitly.
  window.gtag('config', tagId, { send_page_view: false })
}

// Backward-compatible name for feature code and older callers.
export function loadGoogleAdsTag(): void {
  loadGoogleTag()
}

function getLocalStorage(): Storage | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

// Records that a freshly provisioned server is awaiting its deployment
// conversion. Only createdAt/expiresAt are stored — never customer, payment,
// IP, or server configuration data.
export function recordPendingDeployment(serverUuid: string | null | undefined): void {
  if (typeof window === 'undefined') return
  if (!hasMeasurementConsent()) return
  if (!serverUuid || typeof serverUuid !== 'string') return
  const store = getLocalStorage()
  if (!store) return
  const now = Date.now()
  try {
    store.setItem(
      PENDING_PREFIX + serverUuid,
      JSON.stringify({ createdAt: now, expiresAt: now + CONVERSION_TTL_MS })
    )
    googleAdsDebug('pending deployment recorded', { serverUuid })
  } catch {
    // localStorage may be unavailable (private mode, quota) — the conversion
    // simply won't fire, which is safer than double-reporting.
  }
}

function responseBody(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object') return {}
  const outer = value as Record<string, unknown>
  if (outer.data && typeof outer.data === 'object') {
    const data = outer.data as Record<string, unknown>
    if (data.data && typeof data.data === 'object') return data.data as Record<string, unknown>
    return data
  }
  return outer
}

/**
 * Makes registration-created servers eligible for the existing install-complete
 * conversion. This does not report a registration conversion or store customer data.
 */
export function recordPendingRegistrationDeployment(response: unknown): string | null {
  const body = responseBody(response)
  const deployment = body.deployment && typeof body.deployment === 'object'
    ? body.deployment as Record<string, unknown>
    : {}
  const serverUuid = typeof deployment.server_uuid === 'string'
    ? deployment.server_uuid.trim()
    : ''
  if (!serverUuid) return null

  recordPendingDeployment(serverUuid)
  return serverUuid
}

export function clearPendingDeployment(serverUuid: string | null | undefined): void {
  if (!serverUuid || typeof serverUuid !== 'string') return
  const store = getLocalStorage()
  if (!store) return
  try {
    store.removeItem(PENDING_PREFIX + serverUuid)
  } catch {
    // ignore
  }
}

function readPendingStatus(serverUuid: string): 'missing' | 'expired' | 'valid' {
  const store = getLocalStorage()
  if (!store) return 'missing'
  let raw: string | null = null
  try {
    raw = store.getItem(PENDING_PREFIX + serverUuid)
  } catch {
    return 'missing'
  }
  if (!raw) return 'missing'
  try {
    const parsed = JSON.parse(raw) as { expiresAt?: number }
    if (!parsed || typeof parsed.expiresAt !== 'number') {
      clearPendingDeployment(serverUuid)
      return 'missing'
    }
    if (Date.now() > parsed.expiresAt) {
      clearPendingDeployment(serverUuid)
      return 'expired'
    }
    return 'valid'
  } catch {
    clearPendingDeployment(serverUuid)
    return 'missing'
  }
}

export function hasPendingDeployment(serverUuid: string | null | undefined): boolean {
  if (!hasMeasurementConsent()) {
    clearPendingDeployment(serverUuid)
    return false
  }
  if (!serverUuid || typeof serverUuid !== 'string') return false
  return readPendingStatus(serverUuid) === 'valid'
}

function isAlreadyReported(serverUuid: string): boolean {
  const store = getLocalStorage()
  if (!store) return false
  let raw: string | null = null
  try {
    raw = store.getItem(DEDUP_PREFIX + serverUuid)
  } catch {
    return false
  }
  if (!raw) return false
  try {
    const parsed = JSON.parse(raw) as { expiresAt?: number }
    if (parsed && typeof parsed.expiresAt === 'number' && Date.now() > parsed.expiresAt) {
      try {
        store.removeItem(DEDUP_PREFIX + serverUuid)
      } catch {
        // ignore
      }
      return false
    }
    return true
  } catch {
    // A legacy/opaque value still counts as reported.
    return true
  }
}

function markReported(serverUuid: string): void {
  const store = getLocalStorage()
  if (!store) return
  const now = Date.now()
  try {
    store.setItem(
      DEDUP_PREFIX + serverUuid,
      JSON.stringify({ reportedAt: now, expiresAt: now + CONVERSION_TTL_MS })
    )
  } catch {
    // transaction_id still gives Google Ads server-side dedup.
  }
}

function resolveEnablementSkip(): GoogleAdsSkipReason | null {
  if (!hasMeasurementConsent()) return 'measurement_denied'
  const enabled = readConfigValue('GOOGLE_ADS_ENABLED')
  if (enabled !== 'true') return 'disabled'
  if (!isProductionHostname()) return 'wrong_hostname'
  if (!getGoogleAdsId()) return 'missing_ads_id'
  if (!getGoogleAdsDeploymentLabel()) return 'missing_conversion_label'
  return null
}

export function reportGoogleAdsDeploymentConversion(serverUuid: string | null | undefined): void {
  if (typeof window === 'undefined') return

  const skip = (reason: GoogleAdsSkipReason) => {
    googleAdsDebug('conversion skipped', { reason, serverUuid: serverUuid ?? null })
  }

  const enablementSkip = resolveEnablementSkip()
  if (enablementSkip) {
    if (enablementSkip === 'measurement_denied') clearPendingDeployment(serverUuid)
    skip(enablementSkip)
    return
  }

  if (!serverUuid || typeof serverUuid !== 'string') {
    skip('missing_server_uuid')
    return
  }

  // Durable dedup first so a genuine repeat reports the right skip reason.
  if (isAlreadyReported(serverUuid)) {
    clearPendingDeployment(serverUuid)
    skip('already_reported')
    return
  }

  const pending = readPendingStatus(serverUuid)
  if (pending === 'missing') {
    skip('no_pending_deployment')
    return
  }
  if (pending === 'expired') {
    skip('pending_deployment_expired')
    return
  }

  const sendTo = `${getGoogleAdsId()}/${getGoogleAdsDeploymentLabel()}`
  window.gtag?.('event', 'conversion', {
    send_to: sendTo,
    transaction_id: `server-deployment-${serverUuid}`,
  })

  // Queue the durable dedup key, then drop the pending marker only after the
  // conversion has been queued.
  markReported(serverUuid)
  clearPendingDeployment(serverUuid)
  googleAdsDebug('conversion queued', { serverUuid })
}
